import { Controller } from "@hotwired/stimulus"

// Pull-to-refresh for WebView Flutter.
// Attaches to the layout scroll container. Uses capture-phase listeners so that
// preventDefault() fires BEFORE nested overflow-y containers consume the touch.
// Renders a fixed-position overlay positioned just below the page header.
export default class extends Controller {
  static values = {
    threshold: {type: Number, default: 80},
    reloadDelay: {type: Number, default: 300}
  }

  connect() {
    this._startY = 0
    this._pulling = false
    this._triggered = false

    this._onTouchStart = this._handleTouchStart.bind(this)
    this._onTouchMove = this._handleTouchMove.bind(this)
    this._onTouchEnd = this._handleTouchEnd.bind(this)

    this.element.addEventListener("touchstart", this._onTouchStart, { passive: true, capture: true })
    this.element.addEventListener("touchmove", this._onTouchMove, { passive: false, capture: true })
    this.element.addEventListener("touchend", this._onTouchEnd, { passive: true, capture: true })

    this._createIndicator()
  }

  disconnect() {
    this.element.removeEventListener("touchstart", this._onTouchStart, true)
    this.element.removeEventListener("touchmove", this._onTouchMove, true)
    this.element.removeEventListener("touchend", this._onTouchEnd, true)
    this._removeIndicator()
  }

  _handleTouchStart(event) {
    if (this.element.querySelector("[data-pull-to-refresh-disabled]")) return

    this._scrollEl = this._findScrollElement()
      const scrollTop = this._scrollEl.scrollTop || window.pageYOffset || document.documentElement.scrollTop || 0
      if (scrollTop > 0) return

    this._startY = event.touches[0].clientY
    this._pulling = true
    this._triggered = false

    this._syncIndicatorPosition()
  }

  _handleTouchMove(event) {
    if (!this._pulling) return

    const distance = event.touches[0].clientY - this._startY
      const scrollTop = this._scrollEl.scrollTop || window.pageYOffset || document.documentElement.scrollTop || 0

      if (distance < 0 || scrollTop > 0) {
      this._reset()
      return
    }

    event.preventDefault()

    const clamped = Math.min(distance, this.thresholdValue * 1.5)
    const progress = Math.min(distance / this.thresholdValue, 1)

    this._showIndicator(clamped, progress)
    this._triggered = distance >= this.thresholdValue
  }

  _handleTouchEnd() {
    if (!this._pulling) return

    if (this._triggered) {
      this._showRefreshing()
      setTimeout(() => window.location.reload(), this.reloadDelayValue)
    } else {
      this._reset()
    }
  }

  _syncIndicatorPosition() {
    if (!this._indicator) return
    const rect = this.element.getBoundingClientRect()
    this._indicator.style.top = `${rect.top}px`
    this._indicator.style.left = `${rect.left}px`
    this._indicator.style.width = `${rect.width}px`
  }

  _showIndicator(height, progress) {
    if (!this._indicator) return
    this._indicator.style.height = `${height}px`
    this._indicator.style.opacity = `${progress}`
    this._indicator.style.display = "flex"
    this._arrow.style.transform = `rotate(${progress * 180}deg)`
    this._arrow.style.display = ""
    this._spinner.style.display = "none"
  }

  _showRefreshing() {
    if (!this._indicator) return
    this._indicator.style.height = `${this.thresholdValue}px`
    this._indicator.style.opacity = "1"
    this._arrow.style.display = "none"
    this._spinner.style.display = ""
  }

  _reset() {
    this._pulling = false
    this._triggered = false
    if (!this._indicator) return
    this._indicator.style.height = "0px"
    this._indicator.style.opacity = "0"
    this._indicator.style.display = "none"
    this._arrow.style.display = ""
    this._arrow.style.transform = "rotate(0deg)"
    this._spinner.style.display = "none"
  }

  _findScrollElement() {
    const nested = this.element.querySelector(
      ".overflow-y-auto, .overflow-y-scroll"
    )
    if (nested && nested.scrollHeight > nested.clientHeight) return nested
    return this.element
  }

  _createIndicator() {
    const rect = this.element.getBoundingClientRect()

    this._indicator = document.createElement("div")
    Object.assign(this._indicator.style, {
      position: "fixed",
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      height: "0px",
      opacity: "0",
      display: "none",
      alignItems: "center",
      justifyContent: "center",
      zIndex: "9998",
      overflow: "hidden",
      pointerEvents: "none"
    })

    this._arrow = this._createSvg(
      "M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3",
      { transition: "transform 0.1s ease" }
    )

    this._spinner = this._createSpinnerSvg()
    this._spinner.style.display = "none"

    this._indicator.appendChild(this._arrow)
    this._indicator.appendChild(this._spinner)
    document.body.appendChild(this._indicator)
  }

  _createSvg(d, extraStyle = {}) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    svg.setAttribute("viewBox", "0 0 24 24")
    svg.setAttribute("fill", "none")
    svg.setAttribute("stroke", "currentColor")
    svg.setAttribute("stroke-width", "2")
    Object.assign(svg.style, { width: "24px", height: "24px", color: "#0ea5e9", ...extraStyle })

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    path.setAttribute("stroke-linecap", "round")
    path.setAttribute("stroke-linejoin", "round")
    path.setAttribute("d", d)
    svg.appendChild(path)
    return svg
  }

  _createSpinnerSvg() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    svg.setAttribute("viewBox", "0 0 24 24")
    svg.setAttribute("fill", "none")
    Object.assign(svg.style, { width: "24px", height: "24px", color: "#0ea5e9" })
    svg.classList.add("animate-spin")

    const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle")
    circle.setAttribute("cx", "12")
    circle.setAttribute("cy", "12")
    circle.setAttribute("r", "10")
    circle.setAttribute("stroke", "currentColor")
    circle.setAttribute("stroke-width", "4")
    circle.style.opacity = "0.25"

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    path.setAttribute("fill", "currentColor")
    path.setAttribute("d", "M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z")
    path.style.opacity = "0.75"

    svg.appendChild(circle)
    svg.appendChild(path)
    return svg
  }

  _removeIndicator() {
    if (this._indicator?.parentNode) {
      this._indicator.parentNode.removeChild(this._indicator)
    }
    this._indicator = null
  }
}
